import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Joyride, STATUS } from 'react-joyride';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ScrollArea } from './ui/scroll-area';
import { Textarea } from './ui/textarea';
import { 
  LayoutDashboard,
  Database,
  Folder,
  MessageCircle,
  Users,
  Heart,
  Sparkles,
  BookOpen,
  Settings,
  Zap,
  Activity,
  LogOut,
  Plus,
  Search,
  Filter,
  Calendar,
  Upload,
  Send,
  Mic,
  Video,
  FileText,
  CheckCircle2,
  Clock,
  AlertCircle,
  ChevronRight,
  Shield,
  Menu,
  X,
  Image as ImageIcon,
  Music,
  File as FileIcon,
  Utensils,
  Notebook,
  Palette
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { 
  mockDigitalAssets, 
  mockTrustedContacts, 
  mockMessages, 
  mockMemoryVault,
  mockActivityLog,
  mockTriggers,
  mockLegalResources,
  folderStorageOptions,
  myFolders
} from '../mock';
import { useToast } from '../hooks/use-toast';

export const DemoPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [assets, setAssets] = useState(mockDigitalAssets);
  const [contacts, setContacts] = useState(mockTrustedContacts);
  const [messages, setMessages] = useState(mockMessages);
  const [vaultItems, setVaultItems] = useState(mockMemoryVault);
  const [activities, setActivities] = useState(mockActivityLog);
  const [triggers, setTriggers] = useState(mockTriggers);
  
  // Add asset dialog state
  const [showAddAssetDialog, setShowAddAssetDialog] = useState(false);
  const [newAsset, setNewAsset] = useState({
    name: '',
    category: '',
    username: ''
  });
  
  // Welcome instructions dialog
  const [showWelcome, setShowWelcome] = useState(true);
  
  // Storage survey dialog
  const [showStorageSurvey, setShowStorageSurvey] = useState(false);
  const [selectedStorageItems, setSelectedStorageItems] = useState([]);
  const [otherStorageItem, setOtherStorageItem] = useState('');
  const [submittingSurvey, setSubmittingSurvey] = useState(false);
  const [activeFolderName, setActiveFolderName] = useState('');
  
  // Exit waitlist dialog
  const [showExitWaitlist, setShowExitWaitlist] = useState(false);
  const [waitlistName, setWaitlistName] = useState('');
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistAge, setWaitlistAge] = useState('');
  const [waitlistLocation, setWaitlistLocation] = useState('');
  const [waitlistAcceptedPolicy, setWaitlistAcceptedPolicy] = useState(false);
  const [submittingWaitlist, setSubmittingWaitlist] = useState(false);
  const [showExitSuccess, setShowExitSuccess] = useState(false);
  
  // Tour state
  const [runTour, setRunTour] = useState(false);
  const [tourStepIndex, setTourStepIndex] = useState(0);
  
  // Sidebar state (open by default on desktop, closed on mobile)
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 1024;
    }
    return true;
  });

  const handleNavClick = (tab) => {
    setActiveTab(tab);
    // Auto-close sidebar on mobile after selection
    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  const tourSteps = [
    {
      target: '[data-tour="sidebar"]',
      content: 'This is your navigation sidebar. You can access all 10 features of Connect Legacy from here.',
      placement: 'right',
      disableBeacon: true
    },
    {
      target: '[data-tour="nav-dashboard"]',
      content: 'Dashboard shows your overall digital legacy summary—total assets, contacts, messages, and recent activity.',
      placement: 'right',
      disableBeacon: true
    },
    {
      target: '[data-tour="nav-assets"]',
      content: 'My Folder is your personal vault for photos, videos, voice messages, written letters, and memories you want to preserve.',
      placement: 'right',
      disableBeacon: true
    },
    {
      target: '[data-tour="nav-messages"]',
      content: 'Messages lets you send written, voice, or video messages to loved ones—instantly or scheduled for future dates.',
      placement: 'right',
      disableBeacon: true
    },
    {
      target: '[data-tour="nav-contacts"]',
      content: 'Trusted Contacts are the people who can access your digital legacy with permissions you set.',
      placement: 'right',
      disableBeacon: true
    },
    {
      target: '[data-tour="nav-vault"]',
      content: 'Memory Vault stores precious photos, videos, and documents to share with future generations.',
      placement: 'right',
      disableBeacon: true
    },
    {
      target: '[data-tour="nav-ai"]',
      content: 'AI Assistant gives you personalized guidance on preserving memories, writing meaningful messages, and managing your digital legacy.',
      placement: 'right',
      disableBeacon: true
    },
    {
      target: '[data-tour="nav-legal"]',
      content: 'Legal Knowledge provides guides and resources on digital inheritance laws and best practices.',
      placement: 'right',
      disableBeacon: true
    },
    {
      target: '[data-tour="nav-triggers"]',
      content: 'Triggers automate when your legacy is shared—based on inactivity, specific dates, or manual activation.',
      placement: 'right',
      disableBeacon: true
    },
    {
      target: '[data-tour="nav-activity"]',
      content: 'Activity Log tracks every change made to your digital legacy for full transparency.',
      placement: 'right',
      disableBeacon: true
    },
    {
      target: '[data-tour="nav-settings"]',
      content: 'Settings is where you manage your account, security preferences, and notifications.',
      placement: 'right',
      disableBeacon: true
    },
    {
      target: '[data-tour="exit-demo"]',
      content: 'When you\'re done exploring, click here to exit the demo. Remember, this demo is one-time only!',
      placement: 'bottom',
      disableBeacon: true
    }
  ];

  const handleStartTour = () => {
    setShowWelcome(false);
    setActiveTab('dashboard');
    setTimeout(() => {
      setRunTour(true);
      // Auto-click beacon to show tooltip immediately
      setTimeout(() => {
        const beacon = document.querySelector('.react-joyride__beacon');
        if (beacon) beacon.click();
      }, 500);
    }, 300);
  };

  const handleTourCallback = (data) => {
    const { status, type } = data;
    const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];
    if (finishedStatuses.includes(status)) {
      setRunTour(false);
      setTourStepIndex(0);
    }
    // Auto-click new beacons that appear after step transitions
    if (type === 'step:after' || type === 'tooltip:close') {
      setTimeout(() => {
        const beacon = document.querySelector('.react-joyride__beacon');
        if (beacon) beacon.click();
      }, 300);
    }
  };

  useEffect(() => {
    // Mark demo as used
    localStorage.setItem('demoUsed', 'true');
  }, []);

  // Show storage survey shortly after the welcome closes (if user skips tour)
  useEffect(() => {
    // No longer auto-shown — triggered when user clicks a folder card
  }, [showWelcome, runTour]);

  const handleExitDemo = () => {
    setShowExitWaitlist(true);
  };

  const handleSubmitWaitlistFromExit = async (e) => {
    e.preventDefault();
    if (!waitlistName.trim() || !waitlistEmail.trim()) return;
    if (!waitlistAcceptedPolicy) {
      toast({
        title: "Please accept the Privacy Policy",
        description: "You must agree to our Privacy Policy to join the waitlist.",
        variant: "destructive"
      });
      return;
    }

    setSubmittingWaitlist(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      await axios.post(`${API_URL}/api/waitlist`, {
        name: waitlistName.trim(),
        email: waitlistEmail.trim(),
        age: waitlistAge ? parseInt(waitlistAge, 10) : null,
        location: waitlistLocation.trim() || null
      });
      // Clear form, show success dialog
      setWaitlistName('');
      setWaitlistEmail('');
      setWaitlistAge('');
      setWaitlistLocation('');
      setWaitlistAcceptedPolicy(false);
      setShowExitWaitlist(false);
      setShowExitSuccess(true);
    } catch (error) {
      const message = error.response?.data?.detail || "Something went wrong. Please try again.";
      toast({
        title: "Could not join waitlist",
        description: message,
        variant: "destructive"
      });
    } finally {
      setSubmittingWaitlist(false);
    }
  };

  const handleSkipWaitlist = () => {
    setShowExitWaitlist(false);
    navigate('/');
  };

  const handleAddAsset = () => {
    setShowAddAssetDialog(true);
  };

  const handleFolderClick = (folder) => {
    setActiveFolderName(folder.name);
    setSelectedStorageItems([]);
    setOtherStorageItem('');
    setShowStorageSurvey(true);
  };

  const toggleStorageItem = (id) => {
    setSelectedStorageItems(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleSubmitStorageSurvey = async () => {
    if (selectedStorageItems.length === 0 && !otherStorageItem.trim()) {
      toast({
        title: "Pick at least one option",
        description: "Choose something you'd like to store, or write your own.",
        variant: "destructive"
      });
      return;
    }

    setSubmittingSurvey(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const selectedLabels = selectedStorageItems.map(id => {
        const opt = folderStorageOptions.find(o => o.id === id);
        return opt ? opt.label : id;
      });
      await axios.post(`${API_URL}/api/demo-feedback`, {
        selected_items: selectedLabels,
        other_item: otherStorageItem.trim() || null,
        folder_name: activeFolderName || null
      });
      toast({
        title: "Thanks for sharing! 💜",
        description: "Your response helps us build a better Connect Legacy."
      });
      setShowStorageSurvey(false);
      setSelectedStorageItems([]);
      setOtherStorageItem('');
    } catch (error) {
      toast({
        title: "Could not save response",
        description: error.response?.data?.detail || "Please try again.",
        variant: "destructive"
      });
    } finally {
      setSubmittingSurvey(false);
    }
  };

  const handleSaveAsset = () => {
    if (!newAsset.name || !newAsset.category) {
      toast({
        title: "Missing information",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    const asset = {
      id: `custom-${Date.now()}`,
      name: newAsset.name,
      category: newAsset.category,
      icon: 'folder',
      status: 'stored',
      lastAccessed: new Date().toISOString().split('T')[0],
      details: { 
        count: newAsset.username || '—',
        size: '—'
      }
    };

    setAssets([asset, ...assets]);
    
    // Add to activity log
    const activity = {
      id: `activity-${Date.now()}`,
      action: 'Added new item to folder',
      details: newAsset.name,
      timestamp: new Date().toLocaleString(),
      type: 'asset'
    };
    setActivities([activity, ...activities]);

    toast({
      title: "Added successfully! 🎉",
      description: `${newAsset.name} has been added to your folder.`,
    });

    setShowAddAssetDialog(false);
    setNewAsset({ name: '', category: '', username: '' });
    setActiveTab('assets');
  };

  const handleSendMessage = () => {
    toast({
      title: "Message scheduled",
      description: "Your message has been saved and will be delivered according to your preferences.",
    });
  };

  const categoryColors = {
    'Photos': 'bg-pink-100 text-pink-700',
    'Videos': 'bg-blue-100 text-blue-700',
    'Voice Messages': 'bg-purple-100 text-purple-700',
    'Letters': 'bg-amber-100 text-amber-700',
    'Music': 'bg-green-100 text-green-700',
    'Memories': 'bg-rose-100 text-rose-700',
    'Documents': 'bg-slate-100 text-slate-700',
    'Other': 'bg-gray-100 text-gray-700'
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Joyride Guided Tour */}
      <Joyride
        steps={tourSteps}
        run={runTour}
        continuous={true}
        showProgress={true}
        showSkipButton={true}
        scrollToFirstStep={false}
        disableScrolling={true}
        disableBeacon={true}
        spotlightClicks={false}
        callback={handleTourCallback}
        styles={{
          options: {
            primaryColor: '#7E33E0',
            textColor: '#1f2937',
            backgroundColor: '#ffffff',
            arrowColor: '#ffffff',
            overlayColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 10000,
          },
          buttonNext: {
            backgroundColor: '#7E33E0',
            borderRadius: '6px',
            padding: '8px 16px',
            fontSize: '14px',
          },
          buttonBack: {
            color: '#7E33E0',
            fontSize: '14px',
          },
          buttonSkip: {
            color: '#6b7280',
            fontSize: '14px',
          },
          tooltipContainer: {
            textAlign: 'left',
          },
          tooltip: {
            borderRadius: '12px',
            padding: '20px',
          }
        }}
        locale={{
          back: 'Back',
          close: 'Close',
          last: 'Finish Tour',
          next: 'Next',
          skip: 'Skip Tour'
        }}
      />

      {/* Welcome Instructions Dialog */}
      <Dialog open={showWelcome} onOpenChange={setShowWelcome}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="text-2xl flex items-center gap-2">
              <Sparkles className="h-6 w-6 text-[#7E33E0]" />
              Welcome to Connect Legacy Demo
            </DialogTitle>
            <DialogDescription>
              Here's how to make the most of your interactive experience
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-3">
              <div className="flex gap-3 items-start">
                <div className="w-8 h-8 bg-[#7E33E0]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-[#7E33E0]">1</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Explore the Sidebar</p>
                  <p className="text-sm text-gray-600">Navigate through different sections including Dashboard, My Folder, Messages, AI Assistant, and more.</p>
                </div>
              </div>

              <div className="flex gap-3 items-start">
                <div className="w-8 h-8 bg-[#7E33E0]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-[#7E33E0]">2</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Open My Folder</p>
                  <p className="text-sm text-gray-600">Click <strong>My Folder</strong> to see sample folders. <strong>Click any folder</strong> to add what you'd love to store inside.</p>
                </div>
              </div>

              <div className="flex gap-3 items-start">
                <div className="w-8 h-8 bg-[#7E33E0]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-[#7E33E0]">3</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Try the Features</p>
                  <p className="text-sm text-gray-600">Browse sample messages, trusted contacts, memory vault items, and chat with our AI Assistant.</p>
                </div>
              </div>

              <div className="flex gap-3 items-start">
                <div className="w-8 h-8 bg-[#7E33E0]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-[#7E33E0]">4</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Exit When Ready</p>
                  <p className="text-sm text-gray-600">Use the <strong>"Exit Demo"</strong> button at the top right. Remember: this demo is one-time use only!</p>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-4">
              <p className="text-sm text-yellow-900">
                <strong>Note:</strong> This is a concept demo. All sample data is for demonstration purposes only.
              </p>
            </div>
          </div>

          <DialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
            <Button 
              onClick={handleStartTour}
              className="w-full bg-[#7E33E0] hover:bg-[#6B2BC0]"
            >
              <Sparkles className="mr-2 h-4 w-4" />
              Start Guided Tour
            </Button>
            <Button 
              onClick={() => setShowWelcome(false)}
              variant="outline"
              className="w-full"
            >
              Explore On My Own
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add to Folder Dialog */}
      <Dialog open={showAddAssetDialog} onOpenChange={setShowAddAssetDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add to Your Folder</DialogTitle>
            <DialogDescription>
              Store a memory in your folder to experience how Connect Legacy preserves what matters most
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="asset-name">Name *</Label>
              <Input
                id="asset-name"
                placeholder="e.g., Family Trip 2025, Letter to Mom, etc."
                value={newAsset.name}
                onChange={(e) => setNewAsset({ ...newAsset, name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="asset-category">Type *</Label>
              <Select value={newAsset.category} onValueChange={(value) => setNewAsset({ ...newAsset, category: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Photos">Photos</SelectItem>
                  <SelectItem value="Videos">Videos</SelectItem>
                  <SelectItem value="Voice Messages">Voice Messages</SelectItem>
                  <SelectItem value="Letters">Written Letters</SelectItem>
                  <SelectItem value="Music">Music & Playlists</SelectItem>
                  <SelectItem value="Memories">Life Stories & Memories</SelectItem>
                  <SelectItem value="Documents">Personal Documents</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="asset-username">Quick note (Optional)</Label>
              <Input
                id="asset-username"
                placeholder="e.g., A short description"
                value={newAsset.username}
                onChange={(e) => setNewAsset({ ...newAsset, username: e.target.value })}
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-900">
                <strong>Demo Note:</strong> Your information stays only in your browser and will be cleared when you exit the demo.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddAssetDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveAsset} className="bg-[#7E33E0] hover:bg-[#6B2BC0]">
              Add to Folder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Storage Survey Dialog */}
      <Dialog open={showStorageSurvey} onOpenChange={() => { /* mandatory - no close */ }}>
        <DialogContent
          className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto [&>button[aria-label='Close']]:hidden [&_button.absolute.right-4.top-4]:hidden"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="text-2xl flex items-center gap-2">
              <Folder className="h-6 w-6 text-[#7E33E0]" />
              What would you save in your folder?
            </DialogTitle>
            <DialogDescription>
              Pick everything you'd want to save in your folder. (Your response helps us build a better product!)
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              {folderStorageOptions.map((option) => {
                const isSelected = selectedStorageItems.includes(option.id);
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => toggleStorageItem(option.id)}
                    data-testid={`storage-option-${option.id}`}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      isSelected
                        ? 'border-[#7E33E0] bg-[#7E33E0]/5'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`font-medium ${isSelected ? 'text-[#7E33E0]' : 'text-gray-900'}`}>
                        {option.label}
                      </span>
                      {isSelected && <CheckCircle2 className="h-5 w-5 text-[#7E33E0]" />}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="pt-2 space-y-2">
              <Label htmlFor="other-storage" className="text-sm">Something else? Tell us!</Label>
              <Textarea
                id="other-storage"
                placeholder="e.g., Childhood toys photos, family tree, etc."
                value={otherStorageItem}
                onChange={(e) => setOtherStorageItem(e.target.value)}
                data-testid="storage-other-input"
                className="resize-none"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
            <Button
              onClick={handleSubmitStorageSurvey}
              disabled={submittingSurvey}
              data-testid="storage-survey-submit"
              className="w-full bg-[#7E33E0] hover:bg-[#6B2BC0]"
            >
              {submittingSurvey ? 'Sending...' : 'Submit Response'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Exit Success Dialog */}
      <Dialog open={showExitSuccess} onOpenChange={(open) => {
        if (!open) { setShowExitSuccess(false); navigate('/'); }
      }}>
        <DialogContent className="sm:max-w-[480px] text-center">
          <DialogHeader>
            <div className="mx-auto w-16 h-16 bg-[#7E33E0]/10 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 className="h-9 w-9 text-[#7E33E0]" />
            </div>
            <DialogTitle className="text-2xl text-center">You're on the list! 🎉</DialogTitle>
            <DialogDescription className="text-center text-base pt-2">
              Thanks for joining the Connect Legacy waitlist. We'll be in touch soon with updates, early access, and exclusive beta invites.
            </DialogDescription>
          </DialogHeader>
          <div className="pt-4">
            <Button
              onClick={() => { setShowExitSuccess(false); navigate('/'); }}
              className="w-full bg-[#7E33E0] hover:bg-[#6B2BC0]"
              data-testid="exit-success-close"
            >
              Back to Home
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Exit Waitlist Dialog */}
      <Dialog open={showExitWaitlist} onOpenChange={(open) => { if (!open) handleSkipWaitlist(); }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-2xl flex items-center gap-2">
              <Heart className="h-6 w-6 text-[#7E33E0]" />
              Loved the demo?
            </DialogTitle>
            <DialogDescription>
              Join our waitlist and be among the first to experience Connect Legacy when it launches.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmitWaitlistFromExit} className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="wl-name">Your name</Label>
              <Input
                id="wl-name"
                placeholder="Your full name"
                value={waitlistName}
                onChange={(e) => setWaitlistName(e.target.value)}
                required
                data-testid="exit-waitlist-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="wl-email">Email address</Label>
              <Input
                id="wl-email"
                type="email"
                placeholder="you@example.com"
                value={waitlistEmail}
                onChange={(e) => setWaitlistEmail(e.target.value)}
                required
                data-testid="exit-waitlist-email"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="wl-age">Age</Label>
                <Input
                  id="wl-age"
                  type="number"
                  min="1"
                  max="120"
                  placeholder="e.g., 28"
                  value={waitlistAge}
                  onChange={(e) => setWaitlistAge(e.target.value)}
                  data-testid="exit-waitlist-age"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="wl-location">Location</Label>
                <Input
                  id="wl-location"
                  type="text"
                  placeholder="City / Country"
                  value={waitlistLocation}
                  onChange={(e) => setWaitlistLocation(e.target.value)}
                  data-testid="exit-waitlist-location"
                />
              </div>
            </div>

            <div className="flex items-start gap-2 pt-1">
              <Checkbox
                id="exit-accept-policy"
                checked={waitlistAcceptedPolicy}
                onCheckedChange={setWaitlistAcceptedPolicy}
                data-testid="exit-waitlist-policy-checkbox"
                className="mt-0.5 border-gray-400 data-[state=checked]:bg-[#7E33E0] data-[state=checked]:border-[#7E33E0]"
              />
              <Label htmlFor="exit-accept-policy" className="text-xs text-gray-600 leading-relaxed cursor-pointer">
                By signing up, you agree to our{' '}
                <Link
                  to="/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#7E33E0] hover:text-[#9B5BE8] underline"
                >
                  Privacy Policy
                </Link>
                .
              </Label>
            </div>

            <div className="flex flex-col gap-2 pt-2">
              <Button
                type="submit"
                disabled={submittingWaitlist}
                data-testid="exit-waitlist-submit"
                className="w-full bg-[#7E33E0] hover:bg-[#6B2BC0]"
              >
                {submittingWaitlist ? 'Joining...' : 'Join Waitlist'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={handleSkipWaitlist}
                data-testid="exit-waitlist-skip"
                className="w-full"
              >
                Maybe later
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Demo Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="px-3 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                aria-label="Toggle sidebar"
                data-testid="sidebar-toggle"
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
              >
                {sidebarOpen ? <X className="h-5 w-5 text-gray-700" /> : <Menu className="h-5 w-5 text-gray-700" />}
              </button>
              <img src="/assets/cl-heart.png" alt="Connect Legacy" className="h-12 w-12 sm:h-16 sm:w-16 lg:h-20 lg:w-20 object-contain flex-shrink-0" />
              <div className="min-w-0">
                <h1 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 truncate">Connect Legacy</h1>
                <p className="text-xs text-gray-500 truncate">Interactive Demo</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0">
              <Badge variant="outline" className="hidden sm:inline-flex bg-[#7E33E0]/10 text-[#7E33E0] border-[#7E33E0]/20">
                Demo Mode
              </Badge>
              <Button 
                onClick={handleExitDemo}
                variant="ghost"
                size="sm"
                className="text-gray-600 hover:text-gray-900 px-2 sm:px-3"
                data-tour="exit-demo"
              >
                <LogOut className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Exit Demo</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex relative">
        {/* Mobile overlay */}
        {sidebarOpen && (
          <div
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden fixed inset-0 bg-black/40 z-30"
            aria-hidden="true"
          />
        )}

        {/* Sidebar */}
        <aside
          data-tour="sidebar"
          className={`${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0 lg:w-0 lg:border-r-0'
          } fixed lg:sticky top-0 lg:top-[88px] h-[calc(100vh-72px)] lg:h-[calc(100vh-88px)] left-0 z-40 w-64 bg-white border-r border-gray-200 overflow-y-auto transition-all duration-300 ease-in-out pt-16 lg:pt-0 ${
            sidebarOpen ? 'lg:w-64' : 'lg:w-0'
          }`}
        >
          <nav className="p-4 space-y-2">
            <button
              onClick={() => handleNavClick('dashboard')}
              data-tour="nav-dashboard"
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === 'dashboard' 
                  ? 'bg-[#7E33E0] text-white' 
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <LayoutDashboard className="h-5 w-5" />
              <span className="font-medium">Dashboard</span>
            </button>

            <button
              onClick={() => handleNavClick('assets')}
              data-tour="nav-assets"
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === 'assets' 
                  ? 'bg-[#7E33E0] text-white' 
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Folder className="h-5 w-5" />
              <span className="font-medium">My Folder</span>
            </button>

            <button
              onClick={() => handleNavClick('messages')}
              data-tour="nav-messages"
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === 'messages' 
                  ? 'bg-[#7E33E0] text-white' 
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <MessageCircle className="h-5 w-5" />
              <span className="font-medium">Messages</span>
            </button>

            <button
              onClick={() => handleNavClick('contacts')}
              data-tour="nav-contacts"
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === 'contacts' 
                  ? 'bg-[#7E33E0] text-white' 
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Users className="h-5 w-5" />
              <span className="font-medium">Trusted Contacts</span>
            </button>

            <button
              onClick={() => handleNavClick('vault')}
              data-tour="nav-vault"
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === 'vault' 
                  ? 'bg-[#7E33E0] text-white' 
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Heart className="h-5 w-5" />
              <span className="font-medium">Memory Vault</span>
            </button>

            <button
              onClick={() => handleNavClick('ai')}
              data-tour="nav-ai"
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === 'ai' 
                  ? 'bg-[#7E33E0] text-white' 
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Sparkles className="h-5 w-5" />
              <span className="font-medium">AI Assistant</span>
            </button>

            <button
              onClick={() => handleNavClick('legal')}
              data-tour="nav-legal"
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === 'legal' 
                  ? 'bg-[#7E33E0] text-white' 
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <BookOpen className="h-5 w-5" />
              <span className="font-medium">Legal Knowledge</span>
            </button>

            <button
              onClick={() => handleNavClick('triggers')}
              data-tour="nav-triggers"
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === 'triggers' 
                  ? 'bg-[#7E33E0] text-white' 
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Zap className="h-5 w-5" />
              <span className="font-medium">Triggers</span>
            </button>

            <button
              onClick={() => handleNavClick('activity')}
              data-tour="nav-activity"
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === 'activity' 
                  ? 'bg-[#7E33E0] text-white' 
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Activity className="h-5 w-5" />
              <span className="font-medium">Activity Log</span>
            </button>

            <button
              onClick={() => handleNavClick('settings')}
              data-tour="nav-settings"
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === 'settings' 
                  ? 'bg-[#7E33E0] text-white' 
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Settings className="h-5 w-5" />
              <span className="font-medium">Settings</span>
            </button>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto p-8">
          {/* Dashboard */}
          {activeTab === 'dashboard' && (
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Dashboard</h2>
                <p className="text-gray-600">Welcome back! Here's an overview of your digital legacy.</p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card>
                  <CardHeader className="pb-3">
                    <CardDescription>My Folders</CardDescription>
                    <CardTitle className="text-3xl">{myFolders.length}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600">{myFolders.reduce((sum, f) => sum + f.itemCount, 0)} total items stored</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardDescription>Trusted Contacts</CardDescription>
                    <CardTitle className="text-3xl">{contacts.length}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600">{contacts.filter(c => c.verified).length} verified</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardDescription>Scheduled Messages</CardDescription>
                    <CardTitle className="text-3xl">{messages.filter(m => m.status === 'scheduled').length}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600">{messages.length} total messages</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardDescription>Memory Items</CardDescription>
                    <CardTitle className="text-3xl">{vaultItems.reduce((acc, item) => acc + item.itemCount, 0)}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600">In {vaultItems.length} collections</p>
                  </CardContent>
                </Card>
              </div>

              {/* Recent Activity */}
              <Card>
                <CardHeader>
                  <CardTitle>Recent Activity</CardTitle>
                  <CardDescription>Your latest actions</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {activities.slice(0, 5).map((activity) => (
                      <div key={activity.id} className="flex items-start space-x-4 pb-4 border-b last:border-0 last:pb-0">
                        <div className="w-2 h-2 bg-[#7E33E0] rounded-full mt-2"></div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{activity.action}</p>
                          <p className="text-sm text-gray-600">{activity.details}</p>
                          <p className="text-xs text-gray-400 mt-1">{activity.timestamp}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card>
                <CardHeader>
                  <CardTitle>Quick Actions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Button onClick={() => setActiveTab('assets')} variant="outline" className="h-24 flex flex-col items-center justify-center space-y-2">
                      <Folder className="h-6 w-6" />
                      <span>Open My Folder</span>
                    </Button>
                    <Button onClick={() => setActiveTab('messages')} variant="outline" className="h-24 flex flex-col items-center justify-center space-y-2">
                      <MessageCircle className="h-6 w-6" />
                      <span>New Message</span>
                    </Button>
                    <Button onClick={() => setActiveTab('contacts')} variant="outline" className="h-24 flex flex-col items-center justify-center space-y-2">
                      <Users className="h-6 w-6" />
                      <span>Add Contact</span>
                    </Button>
                    <Button onClick={() => setActiveTab('vault')} variant="outline" className="h-24 flex flex-col items-center justify-center space-y-2">
                      <Upload className="h-6 w-6" />
                      <span>Upload Memory</span>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* My Folder */}
          {activeTab === 'assets' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">My Folder</h2>
                  <p className="text-gray-600">Your personal folders for memories. <span className="text-[#7E33E0] font-medium">Click any folder</span> to add what you'd love to store inside.</p>
                </div>
                <Button onClick={() => handleFolderClick({ name: 'New Folder' })} className="bg-[#7E33E0] hover:bg-[#6B2BC0]">
                  <Plus className="h-4 w-4 mr-2" />
                  New Folder
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {myFolders.map((folder) => (
                  <button
                    key={folder.id}
                    onClick={() => handleFolderClick(folder)}
                    data-testid={`folder-card-${folder.id}`}
                    className="text-left bg-white rounded-xl border border-gray-200 p-6 hover:shadow-xl hover:border-[#7E33E0]/30 hover:-translate-y-1 transition-all"
                  >
                    <div className={`w-14 h-14 ${folder.color} rounded-xl flex items-center justify-center mb-4`}>
                      <Folder className={`h-7 w-7 ${folder.iconColor}`} />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{folder.name}</h3>
                    <p className="text-sm text-gray-500 mb-4">{folder.description}</p>
                    <div className="flex items-center justify-between text-xs text-gray-400">
                      <span>{folder.isEmpty ? 'Empty folder' : `${folder.itemCount} items`}</span>
                      <ChevronRight className="h-4 w-4" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Messages */}
          {activeTab === 'messages' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">Messages</h2>
                  <p className="text-gray-600">Send messages to loved ones</p>
                </div>
                <Button className="bg-[#7E33E0] hover:bg-[#6B2BC0]">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Message
                </Button>
              </div>

              <Tabs defaultValue="all" className="w-full">
                <TabsList>
                  <TabsTrigger value="all">All Messages</TabsTrigger>
                  <TabsTrigger value="scheduled">Scheduled</TabsTrigger>
                  <TabsTrigger value="sent">Sent</TabsTrigger>
                </TabsList>

                <TabsContent value="all" className="space-y-4 mt-6">
                  {messages.map((message) => (
                    <Card key={message.id}>
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <CardTitle className="text-lg">{message.subject}</CardTitle>
                            <CardDescription className="mt-2">
                              To: {message.recipient} • {message.type === 'written' ? <FileText className="inline h-4 w-4" /> : message.type === 'video' ? <Video className="inline h-4 w-4" /> : <Mic className="inline h-4 w-4" />} {message.type}
                            </CardDescription>
                          </div>
                          <Badge variant={message.status === 'scheduled' ? 'secondary' : 'default'}>
                            {message.status}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                          <span className="flex items-center">
                            <Calendar className="h-4 w-4 mr-1" />
                            {message.deliveryType === 'specific-date' ? `Deliver on ${message.deliveryDate}` : message.deliveryType === 'on-trigger' ? 'Deliver on trigger' : 'Sent immediately'}
                          </span>
                          <span>Created: {message.createdDate}</span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </TabsContent>

                <TabsContent value="scheduled" className="space-y-4 mt-6">
                  {messages.filter(m => m.status === 'scheduled').map((message) => (
                    <Card key={message.id}>
                      <CardHeader>
                        <CardTitle className="text-lg">{message.subject}</CardTitle>
                        <CardDescription>To: {message.recipient}</CardDescription>
                      </CardHeader>
                    </Card>
                  ))}
                </TabsContent>

                <TabsContent value="sent" className="space-y-4 mt-6">
                  {messages.filter(m => m.status === 'sent').map((message) => (
                    <Card key={message.id}>
                      <CardHeader>
                        <CardTitle className="text-lg">{message.subject}</CardTitle>
                        <CardDescription>To: {message.recipient}</CardDescription>
                      </CardHeader>
                    </Card>
                  ))}
                </TabsContent>
              </Tabs>
            </div>
          )}

          {/* Trusted Contacts */}
          {activeTab === 'contacts' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">Trusted Contacts</h2>
                  <p className="text-gray-600">People who can access your digital legacy</p>
                </div>
                <Button className="bg-[#7E33E0] hover:bg-[#6B2BC0]">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Contact
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {contacts.map((contact) => (
                  <Card key={contact.id}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg">{contact.name}</CardTitle>
                          <CardDescription>{contact.email}</CardDescription>
                        </div>
                        {contact.verified && (
                          <CheckCircle2 className="h-5 w-5 text-green-500" />
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div>
                          <Label className="text-sm text-gray-600">Relationship</Label>
                          <p className="font-medium">{contact.relationship}</p>
                        </div>
                        <div>
                          <Label className="text-sm text-gray-600">Permissions</Label>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {contact.permissions.map((perm, idx) => (
                              <Badge key={idx} variant="secondary">{perm}</Badge>
                            ))}
                          </div>
                        </div>
                        <p className="text-xs text-gray-500">Added: {contact.addedDate}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Memory Vault */}
          {activeTab === 'vault' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">Memory Vault</h2>
                  <p className="text-gray-600">Preserve precious memories for loved ones</p>
                </div>
                <Button className="bg-[#7E33E0] hover:bg-[#6B2BC0]">
                  <Upload className="h-4 w-4 mr-2" />
                  Upload
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {vaultItems.map((item) => (
                  <Card key={item.id} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <CardTitle className="text-lg">{item.title}</CardTitle>
                      <CardDescription className="capitalize">{item.type}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm">
                        <p className="text-gray-600">
                          <span className="font-medium">Items:</span> {item.itemCount}
                        </p>
                        <p className="text-gray-600">
                          <span className="font-medium">Size:</span> {item.size}
                        </p>
                        <p className="text-gray-600">
                          <span className="font-medium">Uploaded:</span> {item.uploadDate}
                        </p>
                        {item.sharedWith.length > 0 && (
                          <div>
                            <Label className="text-xs text-gray-500">Shared with:</Label>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {item.sharedWith.map((person, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs">{person}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* AI Assistant */}
          {activeTab === 'ai' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">AI Assistant</h2>
                <p className="text-gray-600">Get personalized guidance for your digital legacy</p>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Chat with AI Assistant</CardTitle>
                  <CardDescription>Ask questions about preserving memories, writing meaningful messages, and digital legacy planning</CardDescription>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-96 border rounded-lg p-4 mb-4">
                    <div className="space-y-4">
                      <div className="flex items-start space-x-3">
                        <div className="w-8 h-8 bg-[#7E33E0] rounded-full flex items-center justify-center">
                          <Sparkles className="h-4 w-4 text-white" />
                        </div>
                        <div className="flex-1 bg-gray-100 rounded-lg p-3">
                          <p className="text-sm">Hello! I'm your AI assistant for digital legacy planning. How can I help you today?</p>
                        </div>
                      </div>

                      <div className="flex items-start space-x-3 justify-end">
                        <div className="bg-[#7E33E0] text-white rounded-lg p-3">
                          <p className="text-sm">What should I include in my Connect Legacy folder?</p>
                        </div>
                        <div className="w-8 h-8 bg-gray-300 rounded-full"></div>
                      </div>

                      <div className="flex items-start space-x-3">
                        <div className="w-8 h-8 bg-[#7E33E0] rounded-full flex items-center justify-center">
                          <Sparkles className="h-4 w-4 text-white" />
                        </div>
                        <div className="flex-1 bg-gray-100 rounded-lg p-3">
                          <p className="text-sm">Great question! A meaningful Connect Legacy folder typically includes: 1) Cherished photos and videos, 2) Voice and video messages for loved ones, 3) Written letters and life stories, 4) Personal documents like recipes or journals, and 5) Clear instructions on who should access what. Would you like help getting started with any of these?</p>
                        </div>
                      </div>
                    </div>
                  </ScrollArea>

                  <div className="flex space-x-2">
                    <Input placeholder="Type your question..." className="flex-1" />
                    <Button className="bg-[#7E33E0] hover:bg-[#6B2BC0]">
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <div className="grid md:grid-cols-3 gap-4">
                <Card className="cursor-pointer hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <CardTitle className="text-base">Preserving Memories</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600">Learn the fundamentals</p>
                  </CardContent>
                </Card>

                <Card className="cursor-pointer hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <CardTitle className="text-base">Writing Letters Guide</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600">Tips for meaningful messages</p>
                  </CardContent>
                </Card>

                <Card className="cursor-pointer hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <CardTitle className="text-base">Sharing With Loved Ones</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600">How permissions work</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Legal Knowledge */}
          {activeTab === 'legal' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Legal Knowledge</h2>
                <p className="text-gray-600">Resources and guides for digital estate planning</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {mockLegalResources.map((resource) => (
                  <Card key={resource.id} className="hover:shadow-lg transition-shadow cursor-pointer">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <Badge variant="secondary" className="mb-2">{resource.category}</Badge>
                          <CardTitle className="text-lg">{resource.title}</CardTitle>
                          <CardDescription className="mt-2">{resource.description}</CardDescription>
                        </div>
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center text-sm text-gray-500">
                        <Clock className="h-4 w-4 mr-1" />
                        {resource.readTime} read
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Triggers */}
          {activeTab === 'triggers' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">Triggers</h2>
                  <p className="text-gray-600">Automate when your legacy is shared</p>
                </div>
                <Button className="bg-[#7E33E0] hover:bg-[#6B2BC0]">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Trigger
                </Button>
              </div>

              <div className="space-y-4">
                {triggers.map((trigger) => (
                  <Card key={trigger.id}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <CardTitle className="text-lg">{trigger.name}</CardTitle>
                          <CardDescription className="capitalize mt-1">{trigger.type} trigger</CardDescription>
                        </div>
                        <Badge variant={trigger.status === 'active' ? 'default' : 'secondary'} className="bg-green-100 text-green-700">
                          {trigger.status}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div>
                          <Label className="text-sm text-gray-600">Condition</Label>
                          <p className="font-medium">{trigger.condition}</p>
                        </div>
                        <div>
                          <Label className="text-sm text-gray-600">Actions</Label>
                          <ul className="mt-1 space-y-1">
                            {trigger.actions.map((action, idx) => (
                              <li key={idx} className="text-sm flex items-center">
                                <ChevronRight className="h-3 w-3 mr-1 text-[#7E33E0]" />
                                {action}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Activity Log */}
          {activeTab === 'activity' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Activity Log</h2>
                <p className="text-gray-600">Track all changes to your digital legacy</p>
              </div>

              <Card>
                <CardContent className="pt-6">
                  <div className="space-y-6">
                    {activities.map((activity, index) => (
                      <div key={activity.id} className="relative flex items-start space-x-4">
                        {index !== activities.length - 1 && (
                          <div className="absolute left-4 top-8 bottom-0 w-0.5 bg-gray-200"></div>
                        )}
                        <div className="w-8 h-8 bg-[#7E33E0] rounded-full flex items-center justify-center relative z-10">
                          <Activity className="h-4 w-4 text-white" />
                        </div>
                        <div className="flex-1 pb-6">
                          <p className="font-medium text-gray-900">{activity.action}</p>
                          <p className="text-sm text-gray-600 mt-1">{activity.details}</p>
                          <p className="text-xs text-gray-400 mt-2">{activity.timestamp}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Settings */}
          {activeTab === 'settings' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Settings</h2>
                <p className="text-gray-600">Manage your account and preferences</p>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Account Information</CardTitle>
                  <CardDescription>Update your personal details</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Full Name</Label>
                      <Input defaultValue="John Doe" />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input type="email" defaultValue="john.doe@email.com" />
                    </div>
                  </div>
                  <div>
                    <Label>Phone Number</Label>
                    <Input type="tel" defaultValue="+1 (555) 123-4567" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Security Settings</CardTitle>
                  <CardDescription>Manage your security preferences</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Change Password</Label>
                    <Input type="password" placeholder="Enter new password" className="mt-2" />
                  </div>
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <p className="font-medium">Two-Factor Authentication</p>
                      <p className="text-sm text-gray-600">Add an extra layer of security</p>
                    </div>
                    <Button variant="outline">Enable</Button>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Notifications</CardTitle>
                  <CardDescription>Choose what updates you receive</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Email Notifications</p>
                      <p className="text-sm text-gray-600">Receive updates via email</p>
                    </div>
                    <input type="checkbox" defaultChecked className="h-4 w-4" />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Security Alerts</p>
                      <p className="text-sm text-gray-600">Get notified of important security events</p>
                    </div>
                    <input type="checkbox" defaultChecked className="h-4 w-4" />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};
